import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  InputSearchInputEventDetail,
  PIcon,
  PInputSearch,
  POptgroup,
  PSelect,
  PSelectOption,
  PSelectProps,
  SelectToggleEventDetail,
} from '@porsche-design-system/components-react';
import { useEffect, useEffectEvent, useId, useMemo, useState } from 'react';

import { Skeleton } from '../Skeleton';
import { useDebounce } from './useDebounce';

export type AsyncSelectData = {
  options?: {
    label: string;
    value: string;
    appendix?: string;
    disabled?: boolean;
  }[];
  optgroups?: {
    label: string;
    options?: {
      label: string;
      value: string;
      appendix?: string;
      disabled?: boolean;
    }[];
  }[];
  selectedOption?: { label: string; value: string };
  isLoading: boolean;
  isPending: boolean;
  error?: unknown;
};

export type AsyncSelectState = ReturnType<
  typeof useAsyncSelectState
>['selectState'];

/**
 * Owns the search/open state of an async select. Call it together with your
 * data hook and pass the results to `AsyncSelectView`.
 */
export const useAsyncSelectState = (autoSelectSingleOption?: boolean) => {
  const [searchValue, setSearchValue] = useState('');

  const [hasOpened, setHasOpened] = useState(autoSelectSingleOption ?? false);

  const [debouncedSearchValue, isDebouncing] = useDebounce(searchValue, 600);

  return {
    enabled: hasOpened,
    searchString: debouncedSearchValue,
    selectState: { searchValue, setSearchValue, setHasOpened, isDebouncing },
  };
};

export type AsyncSelectViewProps = Omit<PSelectProps, 'onToggle'> & {
  autoSelectSingleOption?: boolean;
  i18n: {
    noData: string;
    error: string;
  };
  data: AsyncSelectData;
  selectState: AsyncSelectState;
  disabledValues?: string[];
};

export const AsyncSelectView = ({
  autoSelectSingleOption,
  i18n,
  disabledValues,
  data: { options, optgroups, selectedOption, isPending, isLoading, error },
  selectState: { searchValue, setSearchValue, setHasOpened, isDebouncing },
  ...props
}: AsyncSelectViewProps) => {
  const isSearchLoading = Boolean(isDebouncing || isLoading || isPending);

  const hasData = Boolean(
    (options && options.length > 0) ||
    (optgroups &&
      optgroups.some((group) => group.options && group.options.length > 0)),
  );

  const shouldShowSkeleton =
    !hasData && (isPending || isLoading) && !error && !searchValue;

  const id = useId();

  const onInput = (e: CustomEvent<InputSearchInputEventDetail>) => {
    const term = (e.target as HTMLElement & { value: string }).value;
    setSearchValue(term);
  };

  const onToggle = (e: CustomEvent<SelectToggleEventDetail>) => {
    if (e.detail.open) {
      setHasOpened(true);
    } else {
      setSearchValue('');
    }
  };

  const onAutoSelect = useEffectEvent((value: string) => {
    props.onChange?.({
      target: { value },
    } as unknown as Parameters<NonNullable<PSelectProps['onChange']>>[0]);
  });
  const singleOptionValue =
    options?.length === 1 && !searchValue ? options?.at(0)?.value : undefined;
  useEffect(() => {
    if (singleOptionValue && autoSelectSingleOption) {
      onAutoSelect(singleOptionValue);
    }
  }, [singleOptionValue, autoSelectSingleOption]);

  return (
    <PSelect {...props} onToggle={onToggle}>
      <PInputSearch
        slot="filter"
        name="search"
        value={searchValue}
        loading={isSearchLoading}
        clear
        indicator
        compact
        autoComplete="off"
        onInput={onInput}
        // Prevent bubbling
        onBlur={(e) => e.stopPropagation()}
        onChange={(e) => e.stopPropagation()}
      />

      <SelectedValue slot="selected">
        {selectedOption ? (
          <SelectedOption>{selectedOption.label}</SelectedOption>
        ) : null}
      </SelectedValue>

      {/* Initial skeleton loading */}
      {shouldShowSkeleton &&
        Array.from({ length: 10 }).map((_, i) => (
          <Skeleton
            data-testid="async-select-skeleton"
            key={`${id}-${i}`}
            css={{
              height: '30px',
              margin: '5px 10px',
            }}
          />
        ))}

      {/* Options */}
      {optgroups
        ? optgroups.map(({ label, options: groupOptions }) => (
            <POptgroup key={`${id}-${label}`} label={label}>
              {groupOptions?.map((opt) => (
                <PSelectOption
                  key={opt.value}
                  value={opt.value}
                  disabled={disabledValues?.includes(opt.value) || opt.disabled}
                >
                  {opt.appendix ? (
                    <OptionColumns
                      selected={opt.value === selectedOption?.value}
                    >
                      {opt.label}
                      <OptionAppendix>{opt.appendix}</OptionAppendix>
                    </OptionColumns>
                  ) : (
                    opt.label
                  )}
                </PSelectOption>
              ))}
            </POptgroup>
          ))
        : options?.map((opt) => (
            <PSelectOption
              key={`${id}-${opt.value}`}
              value={opt.value}
              disabled={disabledValues?.includes(opt.value) || opt.disabled}
            >
              {opt.appendix ? (
                <OptionColumns selected={opt.value === selectedOption?.value}>
                  {opt.label}
                  <OptionAppendix>{opt.appendix}</OptionAppendix>
                </OptionColumns>
              ) : (
                opt.label
              )}
            </PSelectOption>
          ))}

      {/* No filter results */}
      {!isSearchLoading && !hasData && !error && (
        <OptionStatus slot="options-status" role="alert">
          {i18n.noData}
        </OptionStatus>
      )}

      {/* Error state */}
      {error ? (
        <OptionStatus slot="options-status" role="alert">
          <PIcon name="information" color="error" />
          <span>{error instanceof Error ? error.message : i18n.error}</span>
        </OptionStatus>
      ) : null}
    </PSelect>
  );
};

/**
 * This hook is used to transform async data into options and optgroups
 * for the AsyncSelectView component. It also ensures that the currently selected
 * value is included in the options, even if it's not part of the
 * fetched data (e.g. due to filtering or pagination).
 */
export const useAsyncData = <
  TData,
  TOptionDisabledFn extends (opt: TData) => boolean | undefined,
>({
  data,
  selected,
  searchString,
  toOptionFn,
  equalsFn,
  groupByFn,
  groupOrder,
  appendixFn,
  optionDisabledFn,
}: {
  data?: TData[] | null;
  selected?: TData | null;
  searchString?: string;
  toOptionFn: (d: TData) => { label: string; value: string };
  equalsFn?: (a: TData, b: TData) => boolean;
  groupByFn?: (d: TData) => string;
  groupOrder?: string[];
  appendixFn?: (d: TData) => string;
  optionDisabledFn?: TOptionDisabledFn;
}) => {
  const { options, optgroups } = useMemo(() => {
    if ((data === undefined || data === null) && !selected) {
      return {
        options: [],
        optgroups: undefined,
      };
    }

    const dataOptions = [
      ...(data?.some((p) =>
        equalsFn && selected ? equalsFn(p, selected) : p === selected,
      )
        ? []
        : selected && !searchString && data !== undefined && data !== null
          ? [selected]
          : []),
      ...(data ?? []),
    ];

    if (!groupByFn) {
      return {
        options: dataOptions.map((p) => ({
          ...toOptionFn(p),
          appendix: appendixFn?.(p),
        })),
        optgroups: undefined,
      };
    }

    const groups: Record<string, typeof dataOptions> = {};

    dataOptions.forEach((opt) => {
      const group = groupByFn(opt);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(opt);
    });

    return {
      options: dataOptions.map((p) => ({
        ...toOptionFn(p),
        appendix: appendixFn?.(p),
        disabled: optionDisabledFn?.(p),
      })),
      optgroups: Object.entries(groups)
        .toSorted(([labelA], [labelB]) => {
          const indexA = groupOrder?.indexOf(labelA) ?? -1;
          const indexB = groupOrder?.indexOf(labelB) ?? -1;
          if (indexA === -1 && indexB === -1) {
            return 0;
          }
          if (indexA === -1) {
            return 1;
          }
          if (indexB === -1) {
            return -1;
          }
          return indexA - indexB;
        })
        .map(([label, opts]) => ({
          label,
          options: opts.map((p) => ({
            ...toOptionFn(p),
            appendix: appendixFn?.(p),
            disabled: optionDisabledFn?.(p),
          })),
        })),
    };
  }, [
    data,
    selected,
    searchString,
    groupOrder,
    toOptionFn,
    equalsFn,
    groupByFn,
    appendixFn,
    optionDisabledFn,
  ]);

  return {
    options,
    optgroups,
  };
};

const OptionStatus = styled('div', {
  base: {
    display: 'flex',
    gap: 'sm',
    cursor: 'not-allowed',
    color: 'text.contrastMedium',
    padding: 'sm',
  },
});

const OptionColumns = styled('div', {
  base: {
    display: 'grid',
    gap: 'md',
    gridTemplateColumns: '[minmax(50%, 1fr) auto]',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    width: '100%',
    wordBreak: 'break-word',
  },
  variants: {
    selected: {
      false: {
        marginRight: 'lg',
      },
    },
  },
});

const OptionAppendix = styled('span', {
  base: {
    color: 'text.contrastMedium',
    textAlign: 'right',
  },
});

const SelectedValue = styled('span', {
  base: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
});

const SelectedOption = styled('span', {
  base: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});
